import { UnagroError } from '../../data/models/seed-unagro.model';
import { SeedUnagroAviso } from '../../data/schemas/seed-unagro.schema';

export const UNAGRO_DENY_MESSAGE = 'No posee privilegios para acceder al Portal de Semillas';

export type UnagroCategoria = 'planta' | 'expel' | 'curado';

export const CATEGORIAS: readonly UnagroCategoria[] = ['planta', 'expel', 'curado'];

export const CATEGORIA_LABEL: Readonly<Record<UnagroCategoria, string>> = {
  planta: 'Crudo en planta',
  expel: 'Crudo en Expel',
  curado: 'Curado',
};

const CATEGORIA_POR_ESTADO: Readonly<Record<string, UnagroCategoria>> = {
  CRUDO_PLANTA: 'planta',
  CRUDO_EXPEL: 'expel',
  CURADO_EXPEL: 'curado',
  CURADO_PLANTA: 'curado',
};

const ESTADO_LOTE: Readonly<Record<string, string>> = {
  CRUDO_PLANTA: 'Crudo en planta',
  CRUDO_EXPEL: 'Crudo en Expel',
  CURADO: 'Curado',
  MIXTO: 'Repartido',
  EN_CURADO: 'En curado',
  SOLO_MARCA: 'Asignado',
};

const ATRIBUCION: Readonly<Record<string, string>> = {
  MARCA: 'Asignado a UNAGRO',
  DIRECTO: 'Curado asignado a UNAGRO',
  HEREDADO: 'Curado de crudo de UNAGRO',
  PRORRATEADO: 'Curado compartido',
  PRORRATEADO_PROVISORIO: 'Curado compartido (provisorio)',
  VARIAS: 'Varios orígenes',
};

const MOTIVO_BLOQUEO: Readonly<Record<string, { largo: string; corto: string }>> = {
  EN_DESPACHO: { largo: 'Está en un despacho', corto: 'En despacho' },
  CON_RESERVA: { largo: 'Reservado para camión', corto: 'Reservado' },
  CON_TURNO: { largo: 'Turno en playa', corto: 'Turno playa' },
  CON_PRETURNO: { largo: 'Pre-turno a Expel', corto: 'Pre-turno' },
};

const ATRIBUCION_COMPARTIDA = new Set(['PRORRATEADO', 'PRORRATEADO_PROVISORIO']);

export type BloqueoTipo = 'lock' | 'free' | 'pend';

export interface Bloqueo {
  tipo: BloqueoTipo;
  texto: string;
  corto: string;
}

export const UNAGRO_MENSAJE_ERROR: Readonly<Record<UnagroError, { texto: string; reintentar: boolean }>> = {
  comunicacion: {
    texto: 'No se pudo conectar con el servicio. Intente nuevamente en unos minutos.',
    reintentar: true,
  },
  config: {
    texto: 'La planta todavía no está configurada para esta consulta. Contacte a Sistemas',
    reintentar: false,
  },
  contrato: { texto: 'No se pudo obtener el stock de UNAGRO. Contacte a Sistemas.', reintentar: false },
  forbidden: { texto: UNAGRO_DENY_MESSAGE, reintentar: false },
};

export const NOTA_CURADO_COMPARTIDO =
  'Curado compartido con otro destino: solo se muestra la fracción atribuible a UNAGRO.';

export const plural = (n: number, uno: string, otros: string): string => (n === 1 ? uno : otros);

export const kgLabel = (n: number): string => n.toLocaleString('es-AR');

export const categoriaDe = (estado: string): UnagroCategoria | null => CATEGORIA_POR_ESTADO[estado.toUpperCase()] ?? null;

export const estadoLoteLabel = (estado: string): string => ESTADO_LOTE[estado.toUpperCase()] ?? estado;

export const atribucionLabel = (atribucion: string): string => ATRIBUCION[atribucion.toUpperCase()] ?? atribucion;

export const esCuradoCompartido = (atribucion: string): boolean => ATRIBUCION_COMPARTIDA.has(atribucion.toUpperCase());

/** `Bloqueado` S → no se libera (con su motivo), N → sin bloqueos vistos, otro valor → turnos sin revisar. */
export function bloqueoDe(x: { Bloqueado: string; Motivo_Bloqueo: string }): Bloqueo {
  const bloqueado = x.Bloqueado.trim().toUpperCase();
  const motivo = x.Motivo_Bloqueo.trim().toUpperCase();
  if (bloqueado === 'S') {
    const m = MOTIVO_BLOQUEO[motivo];
    return { tipo: 'lock', texto: m?.largo ?? 'No se libera', corto: m?.corto ?? 'No se libera' };
  }
  if (bloqueado === 'N' && motivo !== 'TURNOS_NO_EVALUADOS') {
    return { tipo: 'free', texto: 'Sin bloqueos vistos', corto: 'Sin bloqueos' };
  }
  return { tipo: 'pend', texto: 'Turnos sin revisar', corto: 'Turnos sin revisar' };
}

export function depositoLabel(descripcion: string): string {
  const texto = descripcion.trim().toLowerCase().replace(/\bdeposito\b/g, 'depósito');
  return texto ? texto.charAt(0).toUpperCase() + texto.slice(1) : '';
}

export function camionLabel(estado: string): string {
  const e = estado.trim().toUpperCase();
  if (!e) return 'Pendiente en playa';
  return e === 'SAL' ? 'Salió (liberado)' : `En playa: ${e}`;
}

export function avisoTexto(aviso: SeedUnagroAviso): string {
  const cantidad = /^(\d+)\s+bolson/i.exec(aviso.Texto)?.[1];
  const bolsones = cantidad ? `${cantidad} ${plural(Number(cantidad), 'bolsón', 'bolsones')}` : 'Algunos bolsones';
  switch (aviso.Codigo.toUpperCase()) {
    case 'CONFLICTO_MARCAS':
    case 'MARCA_AMBIGUA':
      return 'Asignación en revisión por AGD: no se computa en el stock hasta su regularización.';
    case 'MARCA_VIEJA':
      return `${bolsones} tuvieron una asignación previa; rige la vigente.`;
    case 'OP_MEZCLADA':
      return NOTA_CURADO_COMPARTIDO;
    case 'OP_SIN_CONSUMO':
    case 'CURADO_NO_ATRIBUIBLE':
      return 'Curado que todavía no puede atribuirse formalmente: en análisis por AGD. No se computa en el stock.';
    case 'CURADO_FUERA_DE_EXPEL':
      return 'Curado estibado fuera del depósito habitual de Expel.';
    case 'RESIDUO_STOCK':
      return 'Remanente de 5 kg o menos: descartado del cómputo.';
    case 'UNIDAD_MIXTA':
      return 'Lote con particularidades de empaque: conviene cotejarlo con AGD.';
    case 'TURNOS_NO_EVALUADOS':
      return 'No se revisaron los turnos de playa en esta consulta.';
    default:
      return aviso.Texto || 'Aviso informativo.';
  }
}
